<template>
    <div class="panorama-page">
        <!-- ============ 顶部栏 ============ -->
        <header class="top-bar">
            <div class="top-left">
                <div class="logo-badge">
                    <i class="el-icon-odometer"></i>
                </div>
                <div class="logo-text">
                    <span class="logo-name">智瞰 · 项目雷达</span>
                    <span class="logo-sub">企业级商业情报智能分析平台</span>
                </div>
                <el-tag class="edition-tag" size="mini" effect="dark">广东版</el-tag>
            </div>
            <div class="top-center">
                <h1 class="main-title">产 品 全 景 驾 驶 舱</h1>
                <div class="main-title-en">PROJECT RADAR · PANORAMA</div>
            </div>
            <div class="top-right">
                <div class="live-badge">
                    <span class="live-dot"></span> LIVE 监测中
                </div>
                <div class="clock">{{ currentTime }}</div>
            </div>
        </header>

        <!-- ============ 实时信号滚动条 ============ -->
        <div class="ticker-bar">
            <div class="ticker-label">
                <i class="el-icon-bell"></i> 实时信号
            </div>
            <div class="ticker-viewport">
                <div class="ticker-track" :style="{ animationDuration: tickerDuration + 's' }">
                    <span v-for="(item, i) in tickerList" :key="'a' + i" class="ticker-item"
                        @click="onTickerClick(item)">
                        <el-tag :color="tickerTagType(item.sourceName)" size="mini" effect="dark" class="ticker-tag">
                            [{{ item.sourceName }}]
                        </el-tag>
                        <span class="ticker-text">{{ item.projectName }}</span>
                        <span v-if="item.money" class="ticker-amount">{{ item.money }} {{ item.moneyUnit }}</span>
                    </span>
                </div>
            </div>
        </div>

        <!-- ============ 主体 ============ -->
        <div class="panorama-body">
            <!-- 内容区 -->
            <div class="panorama-content">
                <!-- 顶部统计卡片 -->
                <div class="stat-cards">
                    <div v-for="(card, i) in statCards" :key="i" class="stat-card panel">
                        <div class="stat-head">
                            <span class="stat-label">{{ card.label }}</span>
                            <span v-if="card.badge" class="stat-badge">{{ card.badge }}</span>
                            <span v-else-if="card.week" class="stat-today">+{{ card.week }} 本周</span>
                        </div>
                        <div class="stat-value">
                            <span class="num">{{ card.value | formatNumber }}</span>
                            <span class="unit">条</span>
                        </div>
                    </div>
                </div>

                <div class="grid-main">
                    <!-- ======== 左列 ======== -->
                    <div class="col-left">
                        <!-- 审批节点阶段分布 -->
                        <div class="panel chart-panel">
                            <div class="panel-title">
                                <span class="title-bar"></span>
                                <span class="title-text">审批节点阶段分布</span>
                                <span class="title-en">PIPELINE</span>
                                <span class="title-extra">在监 <b>{{ pipelineTotal | formatNumber }}</b> 条</span>
                            </div>
                            <div ref="pipelineChart" class="chart chart-pipeline"></div>
                        </div>

                        <!-- 项目行业分布 -->
                        <div class="panel chart-panel">
                            <div class="panel-title">
                                <span class="title-bar"></span>
                                <span class="title-text">项目行业分布</span>
                                <span class="title-en">INDUSTRY</span>
                            </div>
                            <div ref="industryChart" class="chart chart-industry"></div>
                        </div>
                    </div>

                    <!-- ======== 中列：地图 ======== -->
                    <div class="col-center">
                        <div class="panel map-panel">
                            <div class="panel-title">
                                <span class="title-bar"></span>
                                <span class="title-text">广东省项目商机热力图</span>
                                <span class="title-en">21 PREFECTURES</span>
                                <span class="title-extra right">提前 3 - 24 个月捕捉源头信号</span>
                            </div>

                            <div class="map-stat map-stat-top">
                                <div class="map-stat-num">{{ mapStat.todayCount }}</div>
                                <div class="map-stat-desc">今日新捕获项目信号（条）</div>
                            </div>
                            <div ref="mapChart" class="chart chart-map"></div>
                            <div class="map-stat map-stat-bottom">
                                <div class="map-stat-num orange">{{ mapStat.monthCount | formatNumber }}</div>
                                <div class="map-stat-desc">本月新增备案项目（个）</div>
                            </div>

                            <!-- 底部阶段统计条 -->
                            <div class="stage-strip">
                                <div v-for="(s, i) in stageStrip" :key="i" class="stage-item"
                                    :class="{ 'is-hot': s.hot }">
                                    <div class="stage-name">{{ s.name }}</div>
                                    <div class="stage-value">{{ s.count | formatNumber }}<span class="stage-unit"> 条</span></div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- ======== 右列 ======== -->
                    <div class="col-right">
                        <!-- 最新项目动态：无缝自动滚动 -->
                        <div class="panel list-panel">
                            <div class="panel-title">
                                <span class="title-bar"></span>
                                <span class="title-text">最新项目动态</span>
                                <span class="title-en">REALTIME</span>
                                <span class="title-extra right"><i class="el-icon-loading"></i> 滚动播报中</span>
                            </div>
                            <div ref="newsList" class="news-list" @mouseenter="pauseScroll('news')"
                                @mouseleave="resumeScroll('news')">
                                <!-- 数据复制一份，实现无缝循环滚动 -->
                                <div v-for="(item, i) in newsLoop" :key="'n' + i" class="news-item"
                                    @click="onNewsClick(item)">
                                    <el-tag :style="{ width: tagWidth(item.sourceName) }" :color="tickerTagType(item.sourceName)" size="mini" effect="dark"
                                        class="news-tag">{{ item.sourceName }}</el-tag>
                                    <span class="news-city">{{ item.city }}</span>
                                    <span class="news-title">{{ item.projectName }}</span>
                                    <span class="news-amount">{{ item.price }}</span>
                                    <span class="news-time">{{ item.contractTime }}</span>
                                </div>
                            </div>
                        </div>

                        <!-- 招标预告 · 黄金窗口 -->
                        <div class="panel list-panel">
                            <div class="panel-title">
                                <span class="title-bar"></span>
                                <span class="title-text">招标预告 · 黄金窗口</span>
                                <span class="title-en">PRE-TENDER</span>
                            </div>
                            <div ref="pretenderList" class="pretender-list" @mouseenter="pauseScroll('pretender')"
                                @mouseleave="resumeScroll('pretender')">
                                <div v-for="(item, i) in pretenderLoop" :key="'p' + i" class="pretender-item"
                                    @click="onPretenderClick(item)">
                                    <div class="pre-info">
                                        <div class="pre-title">
                                            <span class="pre-city">{{ item.city }}</span>{{ item.projectName }}
                                        </div>
                                    </div>
                                    <div class="pre-date">{{ item.gczDate }}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
/**
 * 产品全景驾驶舱（广东版）
 * 技术栈：Vue2 + Element UI + ECharts
 * 数据：统一从后台接口获取（this.$api.common.home.Chart），在 fetchData → loadAll 中装配
 */
import * as echarts from 'echarts'

/** 广东 21 市经纬度兜底表（GeoJSON 质心不可靠 / 名称对不上时使用） */
const GD_CITY_COORDS = {
    '广州市': [113.26, 23.13], '深圳市': [114.05, 22.55],
    '珠海市': [113.58, 22.27], '汕头市': [116.68, 23.35],
    '佛山市': [113.12, 23.02], '韶关市': [113.60, 24.82],
    '湛江市': [110.36, 21.27], '肇庆市': [112.47, 23.05],
    '江门市': [113.08, 22.58], '茂名市': [110.92, 21.66],
    '惠州市': [114.42, 23.11], '梅州市': [116.12, 24.29],
    '汕尾市': [115.37, 22.79], '河源市': [114.70, 23.74],
    '阳江市': [111.98, 21.86], '清远市': [113.06, 23.68],
    '东莞市': [113.75, 23.04], '中山市': [113.39, 22.52],
    '潮州市': [116.63, 23.66], '揭阳市': [116.37, 23.55],
    '云浮市': [112.04, 22.92],
}
/** 区域热力配色（低 → 高） */
const MAP_HEAT_COLORS = ['#0a2340', '#104a7d', '#0f7ab8', '#00b4e0', '#00ffe0']

/** 信号来源 → 标签配色（9 类各自独立色相，一眼可区分） */
const SOURCE_TAG_COLORS = {
    '备案公开': '#2f6fd6',                               // 蓝
    '审批公告': '#14b8c4',                               // 青
    '招投标': '#d64545',                                 // 红
    '环境影响评价报告': '#3a9e5f',                         // 绿
    '建设用地规划许可证': '#d99a2b',                       // 金橙
    '工程规划许可证': '#8a6fd6',                           // 紫
    '节能审查': '#c94f8c',                               // 玫红
    '核准公告': '#e07b39',                               // 橙
    '建设项目用地预审与选址意见书': '#3d4fc4',             // 靛蓝
}

export default {
    name: 'ProjectPanorama',
    filters: {
        formatNumber(val) {
            if (val === null || val === undefined || val === '') return '--'
            return Number(val).toLocaleString('zh-CN')
        },
    },
    data() {
        return {
            currentTime: '',
            clockTimer: null,
            // 顶部卡片
            statCards: [],
            // 审批节点
            pipelineList: [],
            // 行业分布
            industryList: [],
            // 地图
            mapStat: { todayCount: 0, monthCount: 0 },
            mapCityData: [],
            // 底部阶段条
            stageStrip: [],
            // 滚动信号
            tickerList: [],
            tickerDuration: 30,
            // 动态 / 预告（右侧自动滚动）
            realtimeList: [],
            pretenderList: [],
            // 图表实例
            charts: {},
            resizeHandler: null,
            // 布局变化监听（兜底，防止 canvas 尺寸与容器不一致导致裁切）
            resizeObserver: null,
            // 自动滚动计时器
            scrollTimers: { news: null, pretender: null },
        }
    },
    computed: {
        pipelineTotal() {
            return this.pipelineList.reduce((s, i) => s + (i.value || 0), 0)
        },
        // 数据复制一份实现无缝循环
        newsLoop() {
            return this.realtimeList.length > 1
                ? [...this.realtimeList, ...this.realtimeList]
                : this.realtimeList
        },
        pretenderLoop() {
            return this.pretenderList.length > 1
                ? [...this.pretenderList, ...this.pretenderList]
                : this.pretenderList
        },
    },
    mounted() {
        this.startClock()
        this.fetchData()
        this.resizeHandler = () => this.resizeCharts()
        window.addEventListener('resize', this.resizeHandler)
        // ResizeObserver 兜底：容器尺寸变化（字体加载、布局调整）时自动重绘图表
        if (window.ResizeObserver) {
            this.resizeObserver = new ResizeObserver(() => this.resizeCharts())
            this.resizeObserver.observe(this.$el)
        }
    },
    beforeDestroy() {
        clearInterval(this.clockTimer)
        window.removeEventListener('resize', this.resizeHandler)
        if (this.resizeObserver) this.resizeObserver.disconnect()
        Object.values(this.charts).forEach(c => c && c.dispose())
        Object.values(this.scrollTimers).forEach(t => t && clearInterval(t))
    },
    methods: {
        // ==================== 数据获取 ====================
        fetchData() {
            const url = this.$api.common.home.Chart()
            this.$axios.get(url).then(res => {
                if (res.success && res.data) {
                    this.loadAll(res.data)
                }
            }).catch(() => {})
        },

        loadAll(data) {
            // 顶部统计卡片
            const t = data.total || {}
            const card = (label, key, unit) => ({
                label,
                unit,
                value: (t[key] && t[key].total) || 0,
                week: (t[key] && t[key].week) || 0,
            })
            this.statCards = [
                { ...card('政府审批项目', 'government', '条') },
                { ...card('产业链上游项目', 'agency', '条') },
                { ...card('建设单位档案', 'unit', '家'), badge: '全省覆盖' },
                { ...card('从业人员信息', 'personnel', '人'), badge: '每日更新' },
                { ...card('项目用地', 'land', '宗'), badge: '12万+' },
                { ...card('上市公司投资项目', 'invest', '份'), badge: '持续更新' },
            ]

            // 审批节点阶段分布（兼容 value / count 两种字段）
            this.pipelineList = (data.tableCount && data.tableCount.list || []).map(i => ({
                name: i.name,
                value: i.value != null ? i.value : (i.count || 0),
            }))
            this.renderPipelineChart()

            // 项目行业分布
            this.industryList = (data.industry && data.industry.list || []).map(i => ({
                name: i.name,
                value: i.value != null ? i.value : (i.count || 0),
            }))
            this.renderIndustryChart()

            // 底部阶段条
            this.stageStrip = (data.category && data.category.list) || []

            // 地图统计 + 各市热力
            const map = data.map || {}
            this.mapStat = { todayCount: map.todayNew || 0, monthCount: map.monthNew || 0 }
            this.mapCityData = (map.cityList || []).map(i => ({
                name: i.name,
                value: i.value != null ? i.value : (i.count || 0),
            }))
            this.renderMapChart()

            // 实时信号
            this.tickerList = (data.latestProject && data.latestProject.list) || []
            this.tickerDuration = Math.max(20, this.tickerList.length * 6)

            // 最新项目动态
            this.realtimeList = (data.latestProject && data.latestProject.list) || []

            // 招标预告
            this.pretenderList = (data.latestGcz && data.latestGcz.list) || []

            // 数据渲染完成后，等 DOM 更新再校正一次图表尺寸（防止 canvas 与容器不一致被裁切）
            this.$nextTick(() => {
                this.resizeCharts()
                this.startAutoScroll('news')
                this.startAutoScroll('pretender')
            })
        },

        // ==================== 右侧列表无缝自动滚动 ====================
        startAutoScroll(key) {
            const el = this.$refs[key + 'List']
            if (!el || this.scrollTimers[key]) return
            // 数据过少不滚动
            const half = el.scrollHeight / 2
            if (!half || el.scrollHeight <= el.clientHeight) return
            this.scrollTimers[key] = setInterval(() => {
                // 滚动到一半（数据复制了一份）时无缝回到顶部
                if (el.scrollTop >= half) {
                    el.scrollTop -= half
                }
                el.scrollTop += 0.6
            }, 30)
        },
        pauseScroll(key) {
            clearInterval(this.scrollTimers[key])
            this.scrollTimers[key] = null
        },
        resumeScroll(key) {
            this.startAutoScroll(key)
        },

        // ==================== 顶部时钟 ====================
        startClock() {
            const pad = n => String(n).padStart(2, '0')
            const tick = () => {
                const d = new Date()
                this.currentTime = `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
            }
            tick()
            this.clockTimer = setInterval(tick, 1000)
        },

        // ==================== ECharts ====================
        chartBase() {
            return {
                textStyle: { color: '#8fb3d9', fontFamily: 'Microsoft YaHei' },
            }
        },
        renderPipelineChart() {
            const el = this.$refs.pipelineChart
            if (!el) return
            if (!this.charts.pipeline) this.charts.pipeline = echarts.init(el)
            const count = this.pipelineList.length || 1
            this.charts.pipeline.setOption({
                ...this.chartBase(),
                grid: { left: 8, right: 64, top: 8, bottom: 6, containLabel: true },
                tooltip: {
                    trigger: 'axis',
                    axisPointer: { type: 'shadow' },
                    backgroundColor: 'rgba(6, 24, 48, 0.92)',
                    borderColor: '#1c4f7e',
                    textStyle: { color: '#cfe8ff' },
                },
                xAxis: {
                    type: 'value',
                    splitLine: { lineStyle: { color: 'rgba(60, 120, 180, 0.15)' } },
                    axisLabel: { show: false },
                },
                yAxis: {
                    type: 'category',
                    inverse: true,
                    data: this.pipelineList.map(i => i.name),
                    axisLine: { show: false },
                    axisTick: { show: false },
                    // 长名称截断省略，避免把绘图区挤没；悬浮 tooltip 显示全名
                    axisLabel: {
                        color: '#a8c6e8',
                        fontSize: 11,
                        width: 96,
                        overflow: 'truncate',
                        margin: 8,
                    },
                },
                series: [{
                    type: 'bar',
                    // 类目多的时候自动压扁条形，保证全部可见
                    barWidth: Math.max(6, Math.min(10, Math.floor(220 / count))),
                    barCategoryGap: '30%',
                    data: this.pipelineList.map(i => ({
                        value: i.value,
                        itemStyle: i.name === '招标公告'
                            ? { color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [{ offset: 0, color: '#f5a623' }, { offset: 1, color: '#ffd36b' }]) }
                            : { color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [{ offset: 0, color: '#0e3a63' }, { offset: 1, color: '#19c2ff' }]) },
                    })),
                    label: {
                        show: true, position: 'right',
                        color: '#d6ecff', fontSize: 10,
                        formatter: p => p.value.toLocaleString('zh-CN'),
                    },
                    showBackground: true,
                    backgroundStyle: { color: 'rgba(30, 70, 120, 0.18)', borderRadius: 5 },
                }],
            })
        },

        renderIndustryChart() {
            const el = this.$refs.industryChart
            if (!el) return
            if (!this.charts.industry) this.charts.industry = echarts.init(el)
            const colors = ['#00e5ff', '#3d7fff', '#f5b73d', '#2ee6a8', '#8f7bff', '#ff7b9c', '#5cc8ff', '#9fb7cc']
            this.charts.industry.setOption({
                ...this.chartBase(),
                color: colors,
                tooltip: {
                    trigger: 'item',
                    backgroundColor: 'rgba(6, 24, 48, 0.92)',
                    borderColor: '#1c4f7e',
                    textStyle: { color: '#cfe8ff' },
                    formatter: '{b}<br/>{c} 个（{d}%）',
                },
                legend: {
                    orient: 'vertical',
                    right: 4,
                    top: 'middle',
                    itemWidth: 8,
                    itemHeight: 8,
                    icon: 'rect',
                    // 长行业名截断，防止溢出面板
                    textStyle: { color: '#a8c6e8', fontSize: 10, width: 88, overflow: 'truncate' },
                },
                series: [{
                    type: 'pie',
                    radius: ['42%', '64%'],
                    center: ['31%', '50%'],
                    avoidLabelOverlap: true,
                    itemStyle: { borderColor: '#06182f', borderWidth: 2 },
                    label: { show: false },
                    emphasis: {
                        label: { show: true, color: '#fff', fontSize: 12, formatter: '{b}\n{d}%' },
                    },
                    data: this.industryList,
                }],
            })
        },

        /**
         * 地图渲染
         * 底图只用 geo（区域热力通过 geo.regions 手动插值填色），
         * scatter / effectScatter 挂在 geo 坐标系下，数据带 [经度, 纬度, 数值]；
         * 坐标优先取内置 GD_CITY_COORDS 兜底表，GeoJSON 质心仅作备选。
         * 地图用 layoutCenter + layoutSize 精确适配容器，不再用 zoom 硬放大。
         */
        renderMapChart() {
            const el = this.$refs.mapChart
            if (!el) return
            if (!this.charts.map) this.charts.map = echarts.init(el)

            this.fetchMapGeoJson().then(geoJson => {
                echarts.registerMap('guangdong', geoJson)

                // 归一化名称：去掉"市"后缀，兼容 "广州" / "广州市"
                const strip = n => (n || '').replace(/市$/, '')
                const cityKeys = Object.keys(GD_CITY_COORDS)
                const geoCentroids = this.getCityCoords(geoJson) || {}
                const findCoord = name => {
                    if (geoCentroids[name]) return geoCentroids[name]
                    const hit = cityKeys.find(k => strip(k) === strip(name))
                    return hit ? GD_CITY_COORDS[hit] : null
                }

                const max = Math.max(1, ...this.mapCityData.map(c => c.value || 0))

                // 区域热力填色：按 count 在配色区间插值
                const lerpColor = t => {
                    const pos = Math.min(Math.max(t, 0), 0.999) * (MAP_HEAT_COLORS.length - 1)
                    const i = Math.floor(pos)
                    const c1 = echarts.color.parse(MAP_HEAT_COLORS[i])
                    const c2 = echarts.color.parse(MAP_HEAT_COLORS[i + 1])
                    const rgb = c1.map((v, k) => Math.round(v + (c2[k] - v) * (pos - i)))
                    return `rgb(${rgb[0]},${rgb[1]},${rgb[2]})`
                }
                const regions = this.mapCityData
                    .filter(c => findCoord(c.name))
                    .map(c => ({
                        name: c.name,
                        itemStyle: { areaColor: lerpColor((c.value || 0) / max) },
                    }))

                // 散点数据：[lng, lat, count]
                const scatterData = []
                this.mapCityData.forEach(c => {
                    const coord = findCoord(c.name)
                    if (coord) {
                        scatterData.push({ name: c.name, value: [...coord, c.value] })
                    } else {
                        console.warn('[地图] 未匹配到坐标，已跳过：', c.name)
                    }
                })
                // 头部城市（>= 50% 最大值）加涟漪
                const rippleData = scatterData.filter(d => d.value[2] >= max * 0.5)

                this.charts.map.setOption({
                    ...this.chartBase(),
                    tooltip: {
                        trigger: 'item',
                        backgroundColor: 'rgba(6, 24, 48, 0.92)',
                        borderColor: '#1c4f7e',
                        textStyle: { color: '#cfe8ff' },
                        formatter: p => {
                            const v = Array.isArray(p.value) ? p.value[2] : p.value
                            return `${p.name}<br/>项目信号：<b style="color:#00e5ff">${(v || 0).toLocaleString('zh-CN')}</b> 条`
                        },
                    },
                    geo: {
                        map: 'guangdong',
                        roam: false,
                        // 关键：用 layoutCenter/layoutSize 让地图完整落在容器内，不再 zoom 放大导致上下裁切
                        layoutCenter: ['50%', '50%'],
                        layoutSize: '98%',
                        label: { show: false },
                        itemStyle: {
                            areaColor: 'rgba(12, 38, 71, 0.85)',
                            borderColor: '#2f7fc4',
                            borderWidth: 1,
                            shadowColor: 'rgba(0, 180, 255, 0.35)',
                            shadowBlur: 12,
                        },
                        emphasis: {
                            label: { show: true, color: '#dff4ff', fontSize: 11 },
                            itemStyle: { areaColor: 'rgba(25, 90, 150, 0.9)' },
                        },
                        regions,
                    },
                    series: [
                        {
                            // 普通散点：所有城市
                            type: 'scatter',
                            coordinateSystem: 'geo',
                            data: scatterData,
                            symbolSize: v => 6 + (v[2] / max) * 18,
                            itemStyle: {
                                color: {
                                    type: 'radial', x: 0.5, y: 0.5, r: 0.5,
                                    colorStops: [
                                        { offset: 0, color: 'rgba(0, 255, 230, 0.95)' },
                                        { offset: 0.5, color: 'rgba(0, 190, 255, 0.55)' },
                                        { offset: 1, color: 'rgba(0, 130, 255, 0.05)' },
                                    ],
                                },
                                shadowColor: '#00e5ff',
                                shadowBlur: 10,
                            },
                            label: {
                                show: true,
                                position: 'right',
                                color: '#9fd8ff',
                                fontSize: 10,
                                formatter: p => p.name,
                            },
                            emphasis: { scale: 1.4 },
                            animationDelay: idx => idx * 60,
                            zlevel: 2,
                        },
                        {
                            // 头部城市涟漪散点
                            type: 'effectScatter',
                            coordinateSystem: 'geo',
                            data: rippleData,
                            symbolSize: 8,
                            rippleEffect: {
                                period: 3,
                                scale: 4,
                                brushType: 'stroke',
                                color: '#00ffe0',
                            },
                            itemStyle: { color: '#00ffe0' },
                            zlevel: 3,
                        },
                    ],
                })
            }).catch(() => {
                this.charts.map.setOption({
                    ...this.chartBase(),
                    title: {
                        text: '地图数据加载失败，请检查 map/province/guangdong.json 路径',
                        left: 'center', top: 'middle',
                        textStyle: { color: '#8fb3d9', fontSize: 13, fontWeight: 400 },
                    },
                })
            })
        },

        /** 拉取广东省 GeoJSON（本地静态文件） */
        fetchMapGeoJson() {
            return fetch('map/province/guangdong.json').then(r => {
                if (!r.ok) throw new Error('geojson load failed')
                return r.json()
            })
        },

        /** 从 GeoJSON 计算各城市质心坐标：{ 城市名: [lng, lat] }（仅作兜底） */
        getCityCoords(geoJson) {
            const coords = {}
            ;(geoJson.features || []).forEach(f => {
                const name = f.properties && f.properties.name
                if (!name) return
                const c = this.getPolygonCentroid(f.geometry)
                if (c) coords[name] = c
            })
            return coords
        },

        /** 计算 Polygon / MultiPolygon 最大子块的质心 */
        getPolygonCentroid(geometry) {
            if (!geometry) return null
            const polys = geometry.type === 'Polygon'
                ? [geometry.coordinates]
                : geometry.type === 'MultiPolygon' ? geometry.coordinates : null
            if (!polys) return null
            let best = null, maxArea = -1
            polys.forEach(rings => {
                const ring = rings[0]
                if (!ring || ring.length < 3) return
                let area = 0, cx = 0, cy = 0
                for (let i = 0; i < ring.length - 1; i++) {
                    const [x1, y1] = ring[i], [x2, y2] = ring[i + 1]
                    const f = x1 * y2 - x2 * y1
                    area += f
                    cx += (x1 + x2) * f
                    cy += (y1 + y2) * f
                }
                area /= 2
                const denom = 6 * area
                if (Math.abs(area) > maxArea && Math.abs(denom) > 1e-12) {
                    maxArea = Math.abs(area)
                    best = [cx / denom, cy / denom]
                }
            })
            return best
        },

        resizeCharts() {
            Object.values(this.charts).forEach(c => c && c.resize())
        },

        // ==================== 交互事件 ====================
        /** 信号来源配色：全部返回十六进制色值，配合 el-tag 的 color 属性使用 */
        tickerTagType(type) {
            return SOURCE_TAG_COLORS[type] || '#5a7a9a'
        },
        /** 标签宽度：按字数自适应，封顶 96px，防止长名称撑爆行 */
        tagWidth(name) {
            return Math.min((name || '').length * 12 + 16, 96) + 'px'
        },
        newsTagColor(type) {
            return { '用地': '#b8860b', '环评': '#2e8b57', '立项': '#1f5fbf', '拿地': '#8b6914', '招标': '#a03030', '备案': '#2a7a6f', '开工': '#3a7d44' }[type] || '#3a6ea5'
        },
        onTickerClick(item) {
            this.$emit('signal-click', item)
        },
        onNewsClick(item) {
            // TODO: 跳转项目详情页
            this.$emit('news-click', item)
        },
        onPretenderClick(item) {
            // TODO: 跳转招标预告详情
            this.$emit('pretender-click', item)
        },
    },
}
</script>

<style lang="scss" scoped>
/* ================= 设计变量 ================= */
$bg: #040d1d;
$panel-bg: rgba(9, 25, 48, 0.72);
$panel-border: rgba(45, 120, 190, 0.35);
$cyan: #00e5ff;
$blue: #3d7fff;
$orange: #f5a623;
$text-main: #d6ecff;
$text-sub: #8fb3d9;
$top-h: 48px;      // 顶部栏
$ticker-h: 28px;   // 信号条

/* 注意：本组件依赖全局 body { margin: 0 }（一般在项目 reset.css / index.html 里已重置），
   若页面出现整体滚动条请检查此处。scoped 样式无法影响 body，故不在此声明。 */

.panorama-page {
    width: 100%;
    height: 100vh;              /* 一屏展示：占满视口高度 */
    max-height: 100vh;
    padding: 6px 12px 8px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    background:
        radial-gradient(1200px 500px at 50% -10%, rgba(0, 120, 220, 0.18), transparent 60%),
        radial-gradient(800px 400px at 90% 110%, rgba(0, 229, 255, 0.08), transparent 60%),
        $bg;
    color: $text-main;
    font-family: 'Microsoft YaHei', sans-serif;
    overflow: hidden;           /* 禁止页面级滚动，内部模块各自滚动 */

    /* ---------- 通用面板 ---------- */
    .panel {
        position: relative;
        background: $panel-bg;
        border: 1px solid $panel-border;
        box-shadow: inset 0 0 24px rgba(0, 90, 180, 0.12);
        backdrop-filter: blur(2px);

        /* 四角科技边框 */
        &::before,
        &::after {
            content: '';
            position: absolute;
            width: 12px;
            height: 12px;
            pointer-events: none;
        }
        &::before {
            top: -1px; left: -1px;
            border-top: 2px solid $cyan;
            border-left: 2px solid $cyan;
        }
        &::after {
            bottom: -1px; right: -1px;
            border-bottom: 2px solid $cyan;
            border-right: 2px solid $cyan;
        }
    }

    .panel-title {
        display: flex;
        align-items: center;
        flex-shrink: 0;
        height: 28px;
        padding: 0 10px;
        border-bottom: 1px solid rgba(45, 120, 190, 0.25);
        font-size: 12px;

        .title-bar {
            width: 3px; height: 12px;
            margin-right: 7px;
            background: linear-gradient(180deg, $cyan, $blue);
            box-shadow: 0 0 6px rgba(0, 229, 255, 0.8);
        }
        .title-text { color: #e8f6ff; font-weight: 600; letter-spacing: 1px; }
        .title-en { margin-left: 8px; color: rgba(143, 179, 217, 0.55); font-size: 10px; letter-spacing: 2px; }
        .title-extra {
            margin-left: auto;
            color: $text-sub; font-size: 11px;
            b { color: $cyan; font-weight: 600; }
            &.right { margin-left: auto; }
            i { margin-right: 4px; color: $cyan; }
        }
    }

    .chart { width: 100%; }

    /* ---------- 顶部栏 ---------- */
    .top-bar {
        display: flex;
        align-items: center;
        height: $top-h;
        flex-shrink: 0;
        padding: 0 6px;

        .top-left {
            display: flex; align-items: center; gap: 10px;
            width: 30%;
            .logo-badge {
                width: 32px; height: 32px;
                display: flex; align-items: center; justify-content: center;
                border: 1px solid rgba(0, 229, 255, 0.5);
                border-radius: 50%;
                color: $cyan; font-size: 18px;
                box-shadow: 0 0 12px rgba(0, 229, 255, 0.35);
            }
            .logo-text { display: flex; flex-direction: column; line-height: 1.25; }
            .logo-name { font-size: 16px; font-weight: 700; letter-spacing: 1px; }
            .logo-sub { font-size: 10px; color: $text-sub; }
            .edition-tag {
                background: rgba(245, 166, 35, 0.15);
                border-color: rgba(245, 166, 35, 0.6);
                color: $orange;
            }
        }
        .top-center {
            flex: 1; text-align: center;
            .main-title {
                margin: 0;
                font-size: 22px; font-weight: 700; letter-spacing: 6px;
                background: linear-gradient(180deg, #ffffff, #7cc8ff);
                -webkit-background-clip: text;
                background-clip: text;
                color: transparent;
                text-shadow: 0 0 24px rgba(0, 150, 255, 0.35);
            }
            .main-title-en { margin-top: 1px; font-size: 9px; color: rgba(143, 179, 217, 0.6); letter-spacing: 4px; }
        }
        .top-right {
            width: 30%;
            display: flex; align-items: center; justify-content: flex-end; gap: 14px;
            font-size: 12px;
            .live-badge {
                display: flex; align-items: center; gap: 6px;
                padding: 3px 10px;
                border: 1px solid rgba(46, 230, 168, 0.5);
                color: #2ee6a8; font-size: 11px;
                .live-dot {
                    width: 7px; height: 7px; border-radius: 50%;
                    background: #2ee6a8;
                    animation: live-blink 1.2s infinite;
                }
            }
            .clock { color: #ffe9b0; font-size: 15px; font-weight: 600; letter-spacing: 1px; }
        }
    }
    @keyframes live-blink {
        0%, 100% { opacity: 1; box-shadow: 0 0 6px #2ee6a8; }
        50% { opacity: 0.3; box-shadow: none; }
    }

    /* ---------- 实时信号滚动条 ---------- */
    .ticker-bar {
        display: flex; align-items: center;
        height: $ticker-h;
        margin: 4px 0 8px;
        flex-shrink: 0;
        background: rgba(10, 30, 58, 0.6);
        border: 1px solid rgba(45, 120, 190, 0.3);
        overflow: hidden;

        .ticker-label {
            flex-shrink: 0;
            display: flex; align-items: center; gap: 5px;
            height: 100%; padding: 0 12px;
            background: linear-gradient(90deg, rgba(245, 166, 35, 0.25), rgba(245, 166, 35, 0.05));
            border-right: 1px solid rgba(245, 166, 35, 0.4);
            color: $orange; font-size: 12px; font-weight: 600;
        }
        .ticker-viewport { flex: 1; overflow: hidden; }
        .ticker-track {
            display: inline-flex; align-items: center;
            white-space: nowrap;
            padding-left: 100%;
            animation: ticker-scroll linear infinite;
            &:hover { animation-play-state: paused; }
        }
        .ticker-item {
            display: inline-flex; align-items: center;
            margin-right: 60px;
            font-size: 12px; cursor: pointer;
            .ticker-tag { border: none; margin-right: 6px; color: #fff; }
            .ticker-text { color: $text-main; }
            .ticker-amount { color: $orange; font-weight: 700; margin-left: 4px; }
            &:hover .ticker-text { color: $cyan; }
        }
    }
    @keyframes ticker-scroll {
        from { transform: translateX(0); }
        to { transform: translateX(-100%); }
    }

    /* ---------- 主体布局 ---------- */
    .panorama-body {
        flex: 1;
        min-height: 0;
        display: flex;
    }

    .panorama-content {
        flex: 1;
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 8px;
    }

    /* 顶部统计卡片 */
    .stat-cards {
        display: grid;
        grid-template-columns: repeat(6, 1fr);
        gap: 8px;
        flex-shrink: 0;
        .stat-card {
            padding: 7px 12px 8px;
            .stat-head {
                display: flex; align-items: center;
                font-size: 11px; color: $text-sub;
                .stat-today { margin-left: auto; color: #2ee6a8; font-size: 10px; }
                .stat-badge { margin-left: auto; color: $orange; font-size: 10px; }
            }
            .stat-value {
                margin-top: 3px;
                .num {
                    font-size: 19px; font-weight: 700;
                    color: #fff;
                    text-shadow: 0 0 14px rgba(0, 180, 255, 0.5);
                    font-family: 'DIN Alternate', 'Arial', sans-serif;
                }
                .unit { margin-left: 4px; font-size: 11px; color: $text-sub; }
            }
        }
    }

    /* 三列主网格 */
    .grid-main {
        flex: 1;
        min-height: 0;
        display: grid;
        /* 左列加宽避免图表显示不全，中列地图保持最大占比 */
        grid-template-columns: 23% minmax(0, 1fr) 22%;
        gap: 8px;
    }
    .col-left, .col-center, .col-right {
        display: flex; flex-direction: column; gap: 8px; min-height: 0; min-width: 0;
    }

    .chart-panel { flex: 1; display: flex; flex-direction: column; min-height: 0; overflow: hidden; }
    .chart-pipeline { flex: 1; min-height: 0; }
    .chart-industry { flex: 1; min-height: 0; }

    /* 地图面板 */
    .map-panel {
        flex: 1;
        display: flex;
        flex-direction: column;
        min-height: 0;
        overflow: hidden;
        .chart-map { flex: 1; min-height: 0; }
        .map-stat {
            position: absolute; left: 14px; z-index: 3;
            padding: 7px 12px;
            background: rgba(8, 28, 55, 0.85);
            border: 1px solid rgba(45, 120, 190, 0.4);
            &.map-stat-top { top: 38px; }
            &.map-stat-bottom { bottom: 78px; }
            .map-stat-num {
                font-size: 20px; font-weight: 700; color: $cyan;
                text-shadow: 0 0 12px rgba(0, 229, 255, 0.6);
                &.orange { color: $orange; text-shadow: 0 0 12px rgba(245, 166, 35, 0.6); }
            }
            .map-stat-desc { margin-top: 2px; font-size: 10px; color: $text-sub; }
        }
        .stage-strip {
            display: grid;
            grid-template-columns: repeat(9, 1fr);
            flex-shrink: 0;
            background: rgba(6, 18, 38, 0.75);   /* 不透明底，避免和地图叠在一起显得乱 */
            border-top: 1px solid rgba(45, 120, 190, 0.25);
            .stage-item {
                padding: 5px 2px 6px;
                text-align: center;
                border-right: 1px solid rgba(45, 120, 190, 0.15);
                &:last-child { border-right: none; }
                .stage-name { font-size: 10px; color: $text-sub; white-space: nowrap; overflow: hidden; }
                .stage-value {
                    margin-top: 2px;
                    font-size: 13px; font-weight: 700; color: #cfe8ff;
                    font-family: 'DIN Alternate', 'Arial', sans-serif;
                    .stage-unit { font-size: 10px; color: $text-sub; font-weight: 400; }
                }
                &.is-hot {
                    .stage-name, .stage-value { color: $orange; }
                    .stage-value { text-shadow: 0 0 10px rgba(245, 166, 35, 0.5); }
                }
            }
        }
    }

    /* 右侧列表：填充剩余高度 + 内部滚动 */
    .list-panel {
        flex: 1;
        min-height: 0;
        display: flex;
        flex-direction: column;
        overflow: hidden;
    }
    .news-list {
        flex: 1;
        min-height: 0;
        overflow-y: auto;
        padding: 4px 8px;
        &::-webkit-scrollbar { width: 4px; }
        &::-webkit-scrollbar-thumb { background: rgba(0, 180, 255, 0.3); }
    }
    .news-item {
        display: flex; align-items: center; gap: 6px;
        padding: 6px 4px;
        font-size: 12px;
        cursor: pointer;
        border-bottom: 1px dashed rgba(45, 120, 190, 0.18);
        .news-tag {
            flex-shrink: 0;
            text-align: center;
            border: none;
            font-size: 10px;
            color: #fff;
            white-space: nowrap;
            overflow: hidden;
        }
        .news-city { flex-shrink: 0; color: #7cc8ff; font-weight: 600; }
        .news-title {
            flex: 1; min-width: 0;
            overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
            color: $text-main;
        }
        .news-amount { flex-shrink: 0; color: $orange; font-weight: 700; font-size: 11px; }
        .news-time { flex-shrink: 0; color: rgba(143, 179, 217, 0.6); font-size: 10px; }
        &:hover .news-title { color: $cyan; }
    }

    .pretender-list {
        flex: 1;
        min-height: 0;
        overflow-y: auto;
        padding: 6px 8px;
        &::-webkit-scrollbar { width: 4px; }
        &::-webkit-scrollbar-thumb { background: rgba(0, 180, 255, 0.3); }
    }
    .pretender-item {
        display: flex; align-items: center; gap: 10px;
        padding: 8px 6px;
        margin-bottom: 5px;
        background: rgba(20, 50, 90, 0.25);
        border: 1px solid rgba(45, 120, 190, 0.2);
        cursor: pointer;
        transition: all 0.2s;
        &:hover { border-color: rgba(0, 229, 255, 0.5); background: rgba(0, 120, 220, 0.12); }
        .pre-info { flex: 1; min-width: 0; }
        .pre-title {
            overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
            font-size: 12px; color: $text-main;
            .pre-city { color: #7cc8ff; font-weight: 600; margin-right: 4px; }
        }
        .pre-date { flex-shrink: 0; font-size: 10px; color: rgba(143, 179, 217, 0.65); }
    }

    /* 响应式 */
    @media (max-width: 1600px) {
        .stat-cards { grid-template-columns: repeat(6, 1fr); }
        .grid-main { grid-template-columns: 24% minmax(0, 1fr) 24%; }
    }
    @media (max-width: 1200px) {
        .panorama-page { height: auto; max-height: none; overflow: auto; }
        .grid-main { grid-template-columns: 1fr; }
        .chart-pipeline, .chart-industry, .chart-map { min-height: 260px; }
        .news-list, .pretender-list { flex: none; height: 260px; }
    }
}
</style>